<template>
  <BlocksHeroSection class="bg-cardinal-900 text-white">
    <div class="flex flex-col lg:flex-row justify-between w-full">
      <div class="flex flex-col grow">
        <small class="block text-xs lg:text-sm tracking-normal">
          <a href="#" @click.prevent="$router.back()" class="font-light">
            <i class="fal fa-chevron-left mr-2"></i>
            <span class="underline">Back to Players</span>
          </a>
        </small>
        <div class="my-2 lg:my-4">
          {{ player?.player_name }}
          <span
            v-if="player?.classification"
            class="inline-block ml-2 text-[22px] px-4 py-2 rounded-lg bg-cardinal-600 text-white relative -top-2"
            >{{ player?.classification }}</span
          >
        </div>
        <div
          class="flex dark:text-neutral-200 text-sm md:text-xl font-normal tracking-normal gap-3 md:gap-5"
        >
          <template v-if="player?.hometown">
            {{ player?.hometown }}
          </template>
          <template v-if="player?.position">
            <span class="dark:text-neutral-400">|</span>
            {{ player?.position }}
          </template>
          <template v-if="player?.height">
            <span class="dark:text-neutral-400">|</span>
            {{ inchesToFeetAndInches(player.height) }}
          </template>
          <template v-if="player?.bat || player?.throw">
            <span class="dark:text-neutral-400">|</span>
            Hit/Throw:
            {{ player?.bat ?? '--' }}/{{ player?.throw ?? '--' }}
          </template>
        </div>
      </div>
      <div
        class="flex lg:items-center flex-wrap gap-y-10 mt-10 lg:mt-0 lg:flex-nowrap gap-4 sm:gap-10"
        v-if="player"
      >
        <FormKit
          :classes="{
            outer: 'naked-select !mb-0',
            // wrapper: 'flex items-center',
            selectIcon: '!-ml-0 mr-0',
            inner: '!p-0 !border-0 !shadow-0  !ring-0 !mb-0',
            input: 'w-[90px] !pr-0 !pl-0 !text-3xl !font-bold !text-white',
            label: '!tracking-normal foo-12',
          }"
          type="select"
          label="Rankings for:"
          name="year"
          v-model="statYear"
          :options="rosterYears"
        />
        <DataPoint>
          64 Rank - wRAE
          <template #value>
            <span v-if="!hasAccess">
              <UButton
                class="tracking-normal"
                color="white"
                :to="{ path: '/sign-up' }"
              >
                <i class="fas fa-lock text-cardinal"></i>
                Start Free Trial
              </UButton>
            </span>
            <span
              v-else-if="
                statYearPitching?.integer_rank_weighted_run_allowed_efficiency
              "
            >
              {{
                statYearPitching.integer_rank_weighted_run_allowed_efficiency ||
                '--'
              }}
            </span>
            <span v-else>--</span>
          </template>
        </DataPoint>
        <DataPoint>
          64 Rank - wRCE
          <template #value>
            <span v-if="!hasAccess">
              <UButton
                class="tracking-normal"
                color="white"
                :to="{ path: '/sign-up' }"
              >
                <i class="fas fa-lock text-cardinal"></i>
                Start Free Trial
              </UButton>
            </span>
            <span
              v-else-if="
                statYearHitting?.integer_rank_weighted_run_created_efficiency
              "
            >
              {{
                statYearHitting.integer_rank_weighted_run_created_efficiency ||
                '--'
              }}
            </span>
            <span v-else>--</span>
          </template>
        </DataPoint>
        <DataPoint>
          Team
          <template #value>
            <NuxtLink :to="{ path: `/teams/${statYearTeam?.id}` }">
              <TeamLogo :team="statYearTeam" size="md" />
              <span class="underline hover:no-underline">{{
                statYearTeam?.name
              }}</span>
              <i
                class="far fa-arrow-up-right-from-square ml-3 text-[20px] relative -top-[4px]"
              ></i>
            </NuxtLink>
          </template>
        </DataPoint>
      </div>
    </div>
  </BlocksHeroSection>
  <section class="bg-gray-50 dark:bg-neutral-900 py-4 flex-1">
    <ContainerBasic class="!px-0">
      <div ref="placeholderRef" class="use-sticky-placeholder-ref"></div>
      <div
        ref="tabRef"
        class="w-full shadow transition-transform bg-white dark:bg-neutral-900 z-[4]"
        :class="{
          // '-translate-y-[var(--header-height)]': isSticky && headerVisible,
          'translate-y-[var(--header-height)]': isSticky && headerVisible,
        }"
      >
        <ContainerBasic>
          <div>
            <TabsBasic scroll-to-top v-model="currentTabKey" :tabs="tabs">
              <template #run-expectancy-tab="{ tab }">
                <i
                  v-if="!hasCurrentPortalPass"
                  class="fas fa-lock text-cardinal dark:text-white mr-2"
                />
                {{ tab.label }}
                <UBadge
                  v-if="hasCurrentPortalPass"
                  color="red"
                  size="xs"
                  :label="
                    player?.team?.sport === 'Softball' ? 'Coming Soon' : 'NEW'
                  "
                  class="ml-3"
                />
              </template>
              <template #advanced-stats-tab="{ tab }">
                <i
                  v-if="!hasAccess"
                  class="fas fa-lock text-cardinal dark:text-white mr-2"
                />
                {{ tab.label }}
              </template>
              <template #rankings-tab="{ tab }">
                <i
                  v-if="!hasAccess"
                  class="fas fa-lock text-cardinal dark:text-white mr-2"
                />
                {{ tab.label }}
              </template>
            </TabsBasic>
          </div>
        </ContainerBasic>
      </div>
      <UCard class="pt-5" v-if="currentTab && currentTab.key == 'stats'">
        <HeadingBasic :size="2">Stats</HeadingBasic>
        <UCard class="mt-3 md:mt-8">
          <div class="flex flex-col gap-10">
            <template v-if="hittingStats && hittingStats.length">
              <TableSortable
                :rows="hittingStats"
                :default-columns="defaultBasicHittingCols"
                :selected-columns="selectedBasicHittingColumns"
                :columns="basicHittingCols"
                :dictionary="hittingDictionary"
                reverse-filter-flex-order
                @selected-columns-update="onSelectedBasicHittingColumnsChange"
              >
                <template #title>
                  <HeadingBasic :size="4">Hitting</HeadingBasic>
                </template>
                <template #year-data="{ row }">
                  <TableCellBase left>{{ row.year }}</TableCellBase>
                </template>
                <template #team_id-data="{ row }">
                  <TableCellBase left
                    ><TeamLogo
                      :team="
                        player?.roster?.find(
                          (roster) => roster.year == row.year
                        )?.team
                      "
                    />{{
                      player?.roster?.find((roster) => roster.year == row.year)
                        ?.team?.name
                    }}</TableCellBase
                  >
                </template>
              </TableSortable>
              <div class="flex justify-end mt-3" v-if="!hasAccess">
                <UButton class="bg-cardinal-800" :to="{ path: '/sign-up' }">
                  Sign up for Advanced Statistics
                </UButton>
              </div>
            </template>
            <template v-if="fieldingStats && fieldingStats.length">
              <TableSortable
                :rows="fieldingStats"
                :default-columns="defaultBasicFieldingCols"
                :selected-columns="selectedBasicFieldingColumns"
                :columns="basicFieldingCols"
                :dictionary="fieldingDictionary"
                reverse-filter-flex-order
                @selected-columns-update="onSelectedBasicFieldingColumnsChange"
              >
                <template #title>
                  <HeadingBasic :size="4">Fielding</HeadingBasic>
                </template>
                <template #year-data="{ row }">
                  <TableCellBase left>{{ row.year }}</TableCellBase>
                </template>
                <template #team_id-data="{ row }">
                  <TableCellBase left
                    ><TeamLogo
                      :team="
                        player?.roster?.find(
                          (roster) => roster.year == row.year
                        )?.team
                      "
                    />{{
                      player?.roster?.find((roster) => roster.year == row.year)
                        ?.team?.name
                    }}</TableCellBase
                  >
                </template>
              </TableSortable>
              <div class="flex justify-end mt-3" v-if="!hasAccess">
                <UButton class="bg-cardinal-800" :to="{ path: '/sign-up' }">
                  Sign up for Advanced Statistics
                </UButton>
              </div>
            </template>
            <template v-if="pitchingStats && pitchingStats.length">
              <TableSortable
                :rows="pitchingStats"
                :default-columns="defaultBasicPitchingCols"
                :selected-columns="selectedBasicPitchingColumns"
                :columns="basicPitchingCols"
                :dictionary="pitchingDictionary"
                reverse-filter-flex-order
                @selected-columns-update="onSelectedBasicPitchingColumnsChange"
              >
                <template #title>
                  <HeadingBasic :size="4">Pitching</HeadingBasic>
                </template>
                <template #year-data="{ row }">
                  <TableCellBase left>{{ row.year }}</TableCellBase>
                </template>
                <template #team_id-data="{ row }">
                  <TableCellBase left
                    ><TeamLogo
                      :team="
                        player?.roster?.find(
                          (roster) => roster.year == row.year
                        )?.team
                      "
                    />{{
                      player?.roster?.find((roster) => roster.year == row.year)
                        ?.team?.name
                    }}</TableCellBase
                  >
                </template>
              </TableSortable>
              <div class="flex justify-end mt-3" v-if="!hasAccess">
                <UButton class="bg-cardinal-800" :to="{ path: '/sign-up' }">
                  Sign up for Advanced Statistics
                </UButton>
              </div>
            </template>
          </div>
        </UCard>
      </UCard>
      <UCard
        class="pt-5"
        v-if="currentTab && currentTab.key == 'run-expectancy'"
      >
        <HeadingBasic :size="2">Run Expectancy 24</HeadingBasic>

        <template v-if="player?.team.sport === 'Softball'">
          <div class="mt-10 max-w-[960px]">
            <UCard
              class="relative after:content-[''] after:absolute after:block after:rounded-xl after:z-[1] after:bg-cardinal after:-top-6 after:-left-6 after:w-full after:h-full mt-6 ml-6 p-0 ad-card-wrapper"
            >
              <div
                class="overflow-hidden bg-black relative flex flex-col lg:flex-row gap-10 lg:justify-between lg:items-center rounded-xl p-10 lg:px-10 lg:py-20 z-[2] before:content-[''] before:absolute before:block before:z-[2] before:top-0 before:left-0 before:w-full before:h-full before:bg-[url('/images/64-Analytics_Emblem.png')] before:bg-no-repeat before:bg-center before:opacity-30"
              >
                <div
                  class="flex flex-col lg:flex-row lg:items-center gap-5 relative z-[3]"
                >
                  <img
                    class="z-[1] object-fill max-w-full"
                    width="55px"
                    height="55px"
                    src="~/assets/images/64_Analytics_Emblem_1Color.svg"
                    alt=""
                  />
                  <div class="flex flex-col">
                    <HeadingBasic class="text-white" :size="3">
                      RE24 is coming soon for Softball
                    </HeadingBasic>
                    <p class="text-cardinal-50">
                      Stay up to date on the latest news from 64 Analytics over
                      on X
                    </p>
                  </div>
                </div>
                <a
                  href="https://twitter.com/64Analytics"
                  target="_blank"
                  class="bg-white rounded-full px-4 py-2 self-start font-extrabold flex gap-2 items-center text-cardinal-900 relative z-[2] lg:px-5 lg:py-3 tracking-tight hover:bg-cardinal hover:text-white"
                >
                  Follow us on <i class="fab fa-x-twitter inline-block"></i>
                </a>
              </div>
            </UCard>
          </div>
        </template>
        <template v-else>
          <p class="mt-2">
            All values are calculated using a Run Expectancy matrix created from
            run averages for each of the 24 base-out states.
          </p>
          <!-- <pre>{{ sortedGroups }}</pre> -->
          <UCard class="mt-3 md:mt-8">
            <template v-if="hasCurrentPortalPass">
              <div ref="gameSearchRef"></div>
              <div class="flex items-center gap-5">
                <FiltersPlaysView v-model="params.playsView" />
                <FiltersYearsView v-model="params.year" />
              </div>
              <p class="mt-5 mb-10">
                <template v-if="params.playsView == 'filters'"
                  >Use the filters below to view cumulative situational
                  RE24</template
                >
                <template v-else
                  >Select a game to view {{ player?.player_name }}’s RE24
                  contribution for each play during a game</template
                >
              </p>
              <div
                class="transition-all will-change-[height,opacity]"
                :class="{
                  'h-0 opacity-0 pointer-events-none':
                    params.playsView == 'filters',
                  'h-[112px] opacity-100 pointer-events-auto':
                    params.playsView == 'single-game',
                }"
              >
                <template v-if="teamGameOptions?.length">
                  <FormKit
                    class="opacity-0 absolute top-0 left-0 w-full h-full"
                    type="autocomplete"
                    :listitem-class="'dark:bg-neutral-900'"
                    v-model="params.gameStatsId"
                    label="Search for a Game"
                    placeholder="Select a Game"
                    :options="teamGameOptions || []"
                    :disabled="!teamGameOptions"
                    :reload-on-commit="true"
                    open-on-click
                    popover
                  >
                  </FormKit>
                  <div class="flex items-center gap-3">
                    <UButton
                      :disabled="!canGoToPreviousGame"
                      @click="goToPreviousGame"
                      :class="{
                        'opacity-50 !border !border-gray-200 dark:!border-gray-500 !bg-gray-200 dark:!bg-gray-500':
                          !canGoToPreviousGame,
                        '!border !border-cardinal-400 !text-cardinal dark:!border-white/50 !bg-transparent dark:!text-white':
                          canGoToPreviousGame,
                      }"
                    >
                      <i class="far fa-chevron-left"></i>
                      Previous Game
                    </UButton>
                    <UButton
                      :disabled="!canGoToNextGame"
                      @click="goToNextGame"
                      :class="{
                        'opacity-50 !border-gray-200 dark:!border-gray-500 !bg-gray-200 dark:!bg-gray-500':
                          !canGoToNextGame,
                        '!border !border-cardinal-400 !text-cardinal dark:!border-white/50 !bg-transparent dark:!text-white':
                          canGoToNextGame,
                      }"
                    >
                      Next Game
                      <i class="far fa-chevron-right"></i>
                    </UButton>
                  </div>
                </template>
                <template v-else-if="!teamGamesLoading">
                  <div
                    class="py-5 px-8 border-l border-l-cardinal bg-neutral-50 dark:bg-neutral-950"
                  >
                    <p class="mb-0 font-bold flex items-center gap-2">
                      <i class="far fa-search mr-2"></i>
                      No games found
                    </p>
                  </div>
                </template>
                <template v-else-if="teamGamesLoading">
                  <div
                    class="py-5 px-8 border-l border-l-cardinal bg-neutral-50 dark:bg-neutral-950"
                  >
                    <p class="mb-0 font-bold flex items-center gap-2">
                      <i class="far fa-spinner-third fa-spin mr-2"></i>
                      Loading...
                    </p>
                  </div>
                </template>
              </div>
              <div
                v-if="params.playsView == 'filters'"
                class="flex flex-col gap-2 mt-2 mb-4"
              >
                <div class="flex flex-col md:flex-row gap-20 items-start">
                  <div class="w-full md:w-auto">
                    <div class="mb-6 relative flex items-center gap-3">
                      <HeadingSlim class="relative">Summary</HeadingSlim>
                    </div>

                    <div
                      class="bg-neutral-200 dark:bg-neutral-800 p-5 xl:p-8 rounded-xl flex flex-col gap-6 min-w-[200px] grow md:grow-0"
                    >
                      <span
                        class="text-[72px] font-bold flex flex-col gap-3 leading-none"
                      >
                        <i
                          v-if="teamGamesLoading || teamGamesRefetching"
                          class="fad fa-spinner-third fa-spin self-start"
                        ></i>
                        <template v-else>
                          {{ playsStats.roundedRunExpectancySum }}
                        </template>
                        <small v-if="!teamGamesLoading" class="text-sm relative"
                          >Total RE24</small
                        >
                      </span>
                      <p v-if="!teamGamesLoading" class="flex justify-between">
                        <span>{{ playsStats.totalPlays }} Plays</span>
                        <span>{{ playsStats.totalGames }} GP</span>
                      </p>
                    </div>
                  </div>
                  <div class="w-full">
                    <div class="mb-6 relative flex items-center gap-3">
                      <HeadingSlim class="relative">Filters</HeadingSlim>
                    </div>
                    <div class="flex flex-col gap-4">
                      <FiltersHomeAway
                        v-model="params.homeAway"
                        label="Home vs. Away"
                      />
                      <hr class="border-neutral-700" />
                      <FiltersOpponentConference
                        v-model="params.opponentInOutConference"
                        label="Opp. Conference"
                        class="mt-2"
                      />
                      <hr class="border-neutral-700" />
                      <FiltersConferenceClassification
                        v-model="params.conferenceClassification"
                        label="D1 Class."
                      />
                      <hr class="border-neutral-700" />
                      <FiltersDayOfWeek
                        v-model="params.dayOfWeek"
                        label="Game Day"
                        class="mt-2"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- <pre>playsStats: {{ playsStats }}<br />params: {{ params }}</pre> -->
            <template v-if="!hasCurrentPortalPass">
              <div
                class="upsell-wrapper relative before:w-full before:absolute before:z-[2] before:h-full before:bg-white/[0.75] dark:before:bg-neutral-900/[0.75]"
              >
                <div
                  class="upsell-wrapper__content absolute h-full w-full flex flex-col items-center justify-center z-[3]"
                >
                  <p class="">
                    Unlock the Transfer Portal Pass to view RE24 data
                  </p>
                  <UButton
                    :to="{ path: '/sign-up' }"
                    size="lg"
                    class="mt-5 !bg-cardinal !text-white"
                  >
                    <i class="far fa-lock !text-white"></i>
                    Activate Portal Pass
                  </UButton>
                </div>
                <div style="height: 400px" class="relative z-[1]">
                  <Line :data="reUpsellChartData" :options="reChartOptions" />
                </div>
              </div>
            </template>

            <template v-if="hasCurrentPortalPass">
              <div class="mt-10 mb-6 relative flex items-center gap-3">
                <HeadingSlim class="relative">
                  <template v-if="params.playsView == 'single-game'">
                    Plays
                  </template>
                  <template v-else> Trends </template>
                  <i
                    v-if="teamGamesLoading"
                    class="fad fa-spinner-third fa-spin self-start"
                  ></i
                ></HeadingSlim>
                <Badge
                  color="black"
                  size="sm"
                  :label="filteredGamesPlays?.length"
                />
              </div>
              <template v-if="filteredGamesPlays?.length">
                <div style="height: 400px">
                  <Line :data="reChartData" :options="reChartOptions" />
                </div>
                <template v-if="params.playsView !== 'single-game'">
                  <div class="mt-10 mb-6 relative flex items-center gap-3">
                    <HeadingSlim class="relative"> Games </HeadingSlim>
                    <Badge
                      color="black"
                      size="sm"
                      :label="sortedGroups.length"
                    />
                  </div>
                  <UTable
                    :rows="sortedGroups ?? []"
                    :columns="[
                      {
                        label: '#',
                        key: 'index',
                      },
                      {
                        label: 'Date',
                        key: 'game.date',
                      },
                      {
                        label: 'Opponent',
                        key: 'game.away_team_name',
                      },
                      {
                        label: 'Opp. Conference Classification',
                        key: 'game.away_team.conference.classification',
                      },
                      {
                        label: 'RE24',
                        key: 'sum',
                        // class: 'text-right',
                      },
                      {
                        label: 'Final Score',
                        key: 'game.away_team_score',
                      },
                      {
                        label: 'Actions',
                        key: 'actions',
                      },
                    ]"
                  >
                    <template #index-data="{ row }">
                      {{ row.index }}
                    </template>
                    <template #game.date-data="{ row }">
                      {{ getDayOfWeek(row.game.day_of_week) }},
                      {{ formatDate(row.game.date) }}
                    </template>
                    <template #game.away_team_name-data="{ row }">
                      <strong class="font-bold block">
                        <TeamLogo
                          :team="row.game.away_team"
                          class="inline-block mr-1"
                        />
                        {{ row.game.away_team_name }}
                        <span class="inline-block mx-2">@</span>
                        <TeamLogo
                          :team="row.game.home_team"
                          class="inline-block mr-1"
                        />
                        {{ row.game.home_team_name }}
                        <!-- <template
                        v-if="row.game.home_team_id == reYearRosterTeam?.team_id"
                      >
                        {{ row.game.away_team_name }}
                      </template>
                      <template v-else>
                        @ {{ row.game.home_team_name }}
                      </template> -->
                      </strong>
                    </template>
                    <template
                      #game.away_team.conference.classification-data="{ row }"
                    >
                      <span class="capitalize">
                        <template v-if="teamWasHome(row.game)">
                          {{ row.game.away_team?.conference.abbreviation }} -
                          {{ row.game.away_team?.conference.classification }}
                          {{ row.game.away_team?.conference.division }}
                        </template>
                        <template v-else>
                          {{ row.game.home_team?.conference.abbreviation }} -
                          {{ row.game.home_team?.conference.classification }}
                          {{ row.game.home_team?.conference.division }}
                        </template>
                      </span>
                    </template>
                    <template #game.away_team_score-data="{ row }">
                      <span class="w-8 inlne-block">
                        <template v-if="teamWasHome(row.game)">
                          <template
                            v-if="
                              parseInt(row.game.home_team_score) >
                              parseInt(row.game.away_team_score)
                            "
                            >W</template
                          >
                          <template v-else>L</template>
                        </template>
                        <template v-else>
                          <template
                            v-if="
                              parseInt(row.game.away_team_score) >
                              parseInt(row.game.home_team_score)
                            "
                            >W</template
                          >
                          <template v-else>L</template>
                        </template>
                      </span>
                      &nbsp;
                      {{ row.game.away_team_score }} -
                      {{ row.game.home_team_score }}
                    </template>
                    <template #sum-data="{ row }">
                      <!-- right -->
                      <span
                        :class="{
                          'text-green-600 dark:text-green-400': row.sum > 0,
                          'text-red-600 dark:text-red-400': row.sum < 0,
                          'text-neutral-600 dark:text-neutral-400':
                            row.sum == 0,
                        }"
                      >
                        {{ row.sum.toFixed(3) }}
                      </span>
                    </template>
                    <template #actions-data="{ row }">
                      <UButton
                        color="white"
                        size="sm"
                        @click="viewPlayByPlay(row.game.game_stats_id)"
                      >
                        <i class="far fa-arrow-up-right-from-square"></i>
                        View Play-by-Play
                      </UButton>
                    </template>
                  </UTable>
                </template>
                <template v-else>
                  <PlaysTable
                    class="mt-5 lg:mt-10"
                    :plays="playerPlays?.plays || []"
                    :columns="playerPlaysColumns"
                    :loading="playerPlaysLoading"
                  />
                </template>
              </template>
              <template
                v-else-if="
                  !teamGamesLoading &&
                  params.playsView == 'single-game' &&
                  !playerPlays?.plays.length
                "
              >
                <template
                  v-if="
                    params.playsView == 'single-game' && !params.gameStatsId
                  "
                >
                  Select a game to view at-bats for {{ player?.player_name }}.
                </template>
                <template v-else>
                  {{ player?.player_name }} had no at-bats in this game.
                </template>
              </template>
              <template
                v-else-if="
                  !teamGamesLoading &&
                  params.playsView == 'filters' &&
                  !sortedGroups.length
                "
              >
                {{ player?.player_name }} did not have any at-bats in games
                matching your criteria
              </template>
            </template>

            <!-- {{ params.gameStatsId }}
            {{ playsEnabled }}
            {{ reChartData }}
            {{ reChartOptions }} -->

            <!-- <Line :data="reChartData" :options="reChartOptions" /> -->
            <!-- <div style="width: 100%;"><canvas ref="reChartRef" id="acquisitions"></canvas></div> -->

            <!-- <PlaysTable :plays="playerPlays?.plays || []" :columns="playerPlaysColumns" :loading="playerPlaysLoading" /> -->
            <!-- <pre>{{ teamGameOptions }}</pre>
            <pre>{{ teamGames }}</pre>
            <pre>{{ playerPlays }}</pre> -->
          </UCard>
        </template>
      </UCard>
      <UCard
        class="pt-5"
        v-if="currentTab && currentTab.key == 'advanced-stats'"
      >
        <HeadingBasic :size="2">Advanced Stats</HeadingBasic>
        <UCard class="mt-3 md:mt-8">
          <div class="flex flex-col gap-10">
            <template v-if="hittingStats && hittingStats.length">
              <TableSortable
                :rows="advancedHittingStats"
                :default-columns="defaultAdvancedHittingCols"
                :selected-columns="selectedAdvancedHittingColumns"
                :columns="advancedHittingCols"
                :restricted="!hasAccess"
                :dictionary="hittingDictionary"
                reverse-filter-flex-order
                @selected-columns-update="
                  onSelectedAdvancedHittingColumnsChange
                "
              >
                <template #title>
                  <HeadingBasic :size="4">Hitting</HeadingBasic>
                </template>
                <template #year-data="{ row }">
                  <TableCellBase left>{{ row.year }}</TableCellBase>
                </template>
                <template #team_id-data="{ row }">
                  <TableCellBase left>
                    <TeamLogo
                      :team="
                        player?.roster?.find(
                          (roster) => roster.year == row.year
                        )?.team
                      "
                    />
                    {{
                      player?.roster?.find((roster) => roster.year == row.year)
                        ?.team?.name
                    }}</TableCellBase
                  >
                </template>
              </TableSortable>
            </template>
            <template v-if="pitchingStats && pitchingStats.length">
              <TableSortable
                :rows="advancedPitchingStats"
                :default-columns="defaultAdvancedPitchingCols"
                :selected-columns="selectedAdvancedPitchingColumns"
                :columns="advancedPitchingCols"
                :restricted="!hasAccess"
                :dictionary="pitchingDictionary"
                reverse-filter-flex-order
                @selected-columns-update="
                  onSelectedAdvancedPitchingColumnsChange
                "
              >
                <template #title>
                  <HeadingBasic :size="4">Pitching</HeadingBasic>
                </template>
                <template #year-data="{ row }">
                  <TableCellBase left>{{ row.year }}</TableCellBase>
                </template>
                <template #team_id-data="{ row }">
                  <TableCellBase left
                    ><TeamLogo
                      :team="
                        player?.roster?.find(
                          (roster) => roster.year == row.year
                        )?.team
                      "
                    />{{
                      player?.roster?.find((roster) => roster.year == row.year)
                        ?.team?.name
                    }}</TableCellBase
                  >
                </template>
              </TableSortable>
            </template>
          </div>
        </UCard>
      </UCard>
      <UCard class="pt-5" v-if="currentTab && currentTab.key == 'rankings'">
        <HeadingBasic :size="2">Rankings</HeadingBasic>
        <DataRankingsWidget
          :pitchingBarData="pitchingBarData"
          :hittingBarData="hittingBarData"
          :barYearPitching="barYearPitching"
          :barYearHitting="barYearHitting"
          :pitching-rankings="playerRankPitching"
          :hitting-rankings="playerRankHitting"
          v-model:bar-year="barYear"
          :type="'player'"
          :restricted="!hasAccess"
        />
        <UCard class="mt-3 md:mt-8">
          <div class="flex flex-col gap-10">
            <template v-if="playerRankHitting && playerRankHitting.length">
              <TableSortable
                :default-columns="defaultHittingRankCols"
                :selected-columns="selectedHittingRankColumns"
                :columns="hittingRankCols"
                :rows="playerRankHitting"
                :restricted="!hasAccess"
                :dictionary="hittingDictionary"
                @selected-columns-update="onSelectedHittingRankColumnsChange"
              >
                <template #title>
                  <HeadingBasic :size="4">Hitting</HeadingBasic>
                </template>
                <template #year-data="{ row }">
                  <TableCellBase left>{{ row.year }}</TableCellBase>
                </template>
                <template #team_id-data="{ row }">
                  <TableCellBase left
                    ><TeamLogo
                      :team="
                        player?.roster?.find(
                          (roster) => roster.year == row.year
                        )?.team
                      "
                    />{{
                      player?.roster?.find((roster) => roster.year == row.year)
                        ?.team?.name
                    }}</TableCellBase
                  >
                </template>
              </TableSortable>
            </template>
            <template v-if="playerRankPitching && playerRankPitching.length">
              <TableSortable
                :default-columns="defaultPitchingRankCols"
                :selected-columns="selectedPitchingRankColumns"
                :columns="pitchingRankCols"
                :rows="playerRankPitching"
                :restricted="!hasAccess"
                :dictionary="pitchingDictionary"
                @selected-columns-update="onSelectedPitchingRankColumnsChange"
              >
                <template #title>
                  <HeadingBasic :size="4">Pitching</HeadingBasic>
                </template>
                <template #team_id-data="{ row }">
                  <TableCellBase left
                    ><TeamLogo
                      :team="
                        player?.roster?.find(
                          (roster) => roster.year == row.year
                        )?.team
                      "
                    />{{
                      player?.roster?.find((roster) => roster.year == row.year)
                        ?.team?.name
                    }}</TableCellBase
                  >
                </template>
                <template #year-data="{ row }">
                  <TableCellBase left>{{ row.year }}</TableCellBase>
                </template>
              </TableSortable>
            </template>
          </div>
        </UCard>
      </UCard>
      <!-- <pre class="fixed left-0 bottom-0 z-[3] pointer-events-none bg-white p-2">
        <pre>{{ bounds }}</pre>
        <pre>{{ originalDistance  }}</pre>
        <pre>{{ originalHeight  }}</pre>
      </pre> -->

      <!-- <UTable v-if="player" :rows="[player]"></UTable> -->
    </ContainerBasic>
  </section>
</template>

<script setup lang="ts">
import { basic as hittingColumnsDictionary } from '~/lib/table-columns/player-rank-hitting';
import { basic as pitchingColumnsDictionary } from '~/lib/table-columns/player-rank-pitching';
import type { DbResultOk } from '~/types/database-helpers';
import type { Database } from '~/types';
import {
  basic as basicHittingColumns,
  advanced as advancedHittingColumns,
} from '~/lib/table-columns/hitting';
import {
  basic as basicPitchingColumns,
  advanced as advancedPitchingColumns,
} from '~/lib/table-columns/pitching';
import { basic as basicFieldingColumns } from '~/lib/table-columns/fielding';
import type { ValidTableName } from '~/types/supabase-client';
import type { Column } from '~/types/data-dictionary';
import type { Tables } from '~/types/database-helpers';
import { columns as playerPlaysColumns } from '~/lib/table-columns/player-plays';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  type ChartData,
} from 'chart.js';
import { Line } from 'vue-chartjs';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// import * as chartConfig from './chartConfig.js'

const supabase = useSupabaseClient<Database>();
const route = useRoute();
const router = useRouter();
const store = useProfileStore();
const { hasAccess, hasCurrentPortalPass } = storeToRefs(store);
const tabRef = ref<Element | null>(null);
const placeholderRef = ref<Element | null>(null);
const { bounds, originalDistance, originalHeight, isSticky } = useSticky(
  tabRef,
  placeholderRef
);
const { headerVisible } = useHeadroom();

const playerId = route.params.id.toString();
const currentTabKey = ref(route.query.tab?.toString() || 'stats');

const { fillQueryParamValuesFromRefs, stopWatchers } = useSyncWithQuery<string>(
  [{ key: 'tab', ref: currentTabKey }]
);

onBeforeRouteLeave(() => {
  stopWatchers();
});

onMounted(() => {
  fillQueryParamValuesFromRefs();
});

const currentTab = computed(() =>
  tabs.find((tab) => tab.key == currentTabKey.value)
);

const statYear = ref(2025);
const barYear = ref(2025);

const teamWasHome = (game: Partial<Tables<'games'>>) => {
  return game.home_team_id == player.value?.team_id;
};

const hittingBarData = computed(
  (): {
    label: string;
    value: number;
    rawValue: number | string;
  }[] => {
    const hittingRankColsWithPercentiles = [];
    const percentileFields = [
      'percentile_rank_on_base_plus_slugging',
      'percentile_rank_weighted_on_base_average',
      'percentile_rank_isolated_power',
      'percentile_rank_weighted_runs_created',
      'percentile_rank_weighted_runs_above_average',
      'percentile_rank_runs_plate_appearance',
      'percentile_rank_runs_created_ab',
      'percentile_rank_weighted_run_created_efficiency',
    ];
    return percentileFields.map((percentileField) => {
      const field = percentileField.replaceAll('percentile_rank_', '');
      const dictionaryRow = hittingRankDictionary.value.find(
        (row) => row.characteristic == field
      );
      return {
        label: dictionaryRow?.abbreviation,
        value: barYearHitting.value
          ? (barYearHitting.value[percentileField] * 100).toFixed(2)
          : '',
        rawValue: playerRankHitting.value
          ? formatHittingData(
              dictionaryRow,
              barYearHitting.value
                ? barYearHitting.value[dictionaryRow?.characteristic]
                : ''
            )
          : '',
      };
    });
  }
);
const pitchingBarData = computed(
  (): {
    label: string;
    value: number;
    rawValue: number | string;
  }[] => {
    const pitchingRankColsWithPercentiles = [];
    const percentileFields = [
      'percentile_rank_on_base_plus_slugging_against',
      'percentile_rank_batting_average_on_balls_in_play',
      'percentile_rank_strikeout_to_walk_ratio',
      'percentile_rank_walks_plus_hits_per_inning_pitched',
      'percentile_rank_fielding_independent_pitching',
      'percentile_rank_skill_interactive_earned_run_average',
      'percentile_rank_pitches_per_inning',
      'percentile_rank_runs_allowed_ab',
      'percentile_rank_weighted_run_allowed_efficiency',
    ];
    return percentileFields.map((percentileField) => {
      const field = percentileField.replaceAll('percentile_rank_', '');
      const dictionaryRow = pitchingRankDictionary.value.find(
        (row) => row.characteristic == field
      );
      return {
        label: dictionaryRow?.abbreviation,
        value: barYearPitching.value
          ? (barYearPitching.value[percentileField] * 100).toFixed(2)
          : '',
        rawValue: barYearPitching.value
          ? formatPitchingData(
              dictionaryRow,
              barYearPitching.value
                ? barYearPitching.value[dictionaryRow?.characteristic]
                : ''
            )
          : '',
      };
    });
  }
);

const statYearTeam = computed(() => {
  return player.value?.roster?.find((roster) => roster.year == statYear.value)
    ?.team;
});

const rosterYears = computed(() => {
  if (!player.value?.roster) return [2025, 2024, 2023, 2022, 2021];
  const years = player.value.roster
    .map((r) => r.year)
    .filter(Boolean)
    .sort((a, b) => b - a);
  return years.length ? years : [2025, 2024, 2023, 2022, 2021];
});

const tabs = [
  {
    key: 'stats',
    label: 'Stats',
    description: '',
  },
  {
    key: 'run-expectancy',
    label: 'Run Expectancy',
    description: 'members-only run-expectancy',
  },
  {
    key: 'advanced-stats',
    label: 'Advanced Stats',
    description: 'members-only statistics',
  },
  {
    key: 'rankings',
    label: 'Rankings',
    description: 'members-only rankings',
  },
];

const HITTING_TABLE_NAMES: ValidTableName[] = ['hitting'];
const {
  parseData: hittingParseData,
  localDictionary: hittingDictionary,
  formatData: formatHittingData,
} = useDataDictionary(HITTING_TABLE_NAMES);

// Basic Hitting Columns
const defaultBasicHittingCols = transformColumns(
  basicHittingColumns,
  hittingDictionary.value
);
const selectedBasicHittingColumns = ref([...defaultBasicHittingCols]);
const basicHittingCols = computed((): Column[] => {
  return defaultBasicHittingCols.filter((column) => {
    return selectedBasicHittingColumns.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedBasicHittingColumnsChange = ($event: Column[]) => {
  selectedBasicHittingColumns.value = $event;
};

// Advanced Hitting Columns
const defaultAdvancedHittingCols = transformColumns(
  advancedHittingColumns,
  hittingDictionary.value
);
const selectedAdvancedHittingColumns = ref([...defaultAdvancedHittingCols]);
const advancedHittingCols = computed((): Column[] => {
  return defaultAdvancedHittingCols.filter((column) => {
    return selectedAdvancedHittingColumns.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedAdvancedHittingColumnsChange = ($event: Column[]) => {
  selectedAdvancedHittingColumns.value = $event;
};

// Basic Pitching Columns
const PITCHING_TABLE_NAMES: ValidTableName[] = ['pitching', 'pitching_team'];
const {
  parseData: pitchingParseData,
  localDictionary: pitchingDictionary,
  formatData: formatPitchingData,
} = useDataDictionary(PITCHING_TABLE_NAMES);
// const basicPitchingCols = computed(() => {
//   return transformColumns(basicPitchingColumns, pitchingDictionary.value);
// });
// const advancedPitchingCols = computed(() => {
//   return transformColumns(advancedPitchingColumns, pitchingDictionary.value);
// });

// Basic Pitching Columns
const defaultBasicPitchingCols = transformColumns(
  basicPitchingColumns,
  hittingDictionary.value
);
const selectedBasicPitchingColumns = ref([...defaultBasicPitchingCols]);
const basicPitchingCols = computed((): Column[] => {
  return defaultBasicPitchingCols.filter((column) => {
    return selectedBasicPitchingColumns.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedBasicPitchingColumnsChange = ($event: Column[]) => {
  selectedBasicPitchingColumns.value = $event;
};

// Advanced Pitching Columns
const defaultAdvancedPitchingCols = transformColumns(
  advancedPitchingColumns,
  pitchingDictionary.value
);
const selectedAdvancedPitchingColumns = ref([...defaultAdvancedPitchingCols]);
const advancedPitchingCols = computed((): Column[] => {
  return defaultAdvancedPitchingCols.filter((column) => {
    return selectedAdvancedPitchingColumns.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedAdvancedPitchingColumnsChange = ($event: Column[]) => {
  selectedAdvancedPitchingColumns.value = $event;
};

// Basic Fielding Columns
const FIELDING_TABLE_NAMES: ValidTableName[] = ['fielding'];
const { parseData: fieldingParseData, localDictionary: fieldingDictionary } =
  useDataDictionary(FIELDING_TABLE_NAMES);
const defaultBasicFieldingCols = transformColumns(
  basicFieldingColumns,
  fieldingDictionary.value
);
const selectedBasicFieldingColumns = ref([...defaultBasicFieldingCols]);
const basicFieldingCols = computed((): Column[] => {
  return defaultBasicFieldingCols.filter((column) => {
    return selectedBasicFieldingColumns.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedBasicFieldingColumnsChange = ($event: Column[]) => {
  selectedBasicFieldingColumns.value = $event;
};

/**
 * Player Rank Hitting
 */
const HR_TABLE_NAMES: ValidTableName[] = ['hitting', 'player_rank'];
const { parseData: hrParseData, localDictionary: hittingRankDictionary } =
  useDataDictionary(HR_TABLE_NAMES);
useDataDictionary(HR_TABLE_NAMES);
const defaultHittingRankCols = (() => {
  const cols = [...hittingColumnsDictionary].filter(
    (col) =>
      !['player_name', 'team_name', 'position', 'classification'].includes(
        col.key
      )
  );
  cols.splice(1, 0, {
    key: 'team_id',
    label: 'School',
    sortable: true,
    class: '!text-left',
    enabledByDefault: true,
  });
  return transformColumns(cols, hittingRankDictionary.value);
})();
const selectedHittingRankColumns = ref([...defaultHittingRankCols]);
const hittingRankCols = computed((): Column[] => {
  return defaultHittingRankCols.filter((column) => {
    return selectedHittingRankColumns.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedHittingRankColumnsChange = ($event: Column[]) => {
  selectedHittingRankColumns.value = $event;
};
const getPlayerRankHittingData = async () => {
  if (!hasAccess.value) {
    const fakeRows = [];
    for (var i = 0; i < 4; i++) {
      const fakeRow = {} as Record<any, any>;
      hittingRankCols.value?.map((col, index) => {
        fakeRow[col.key] =
          index == 0 ? 2024 : index == 1 ? 'Sign up for access ' : '0.000';
      });
      fakeRows.push(fakeRow);
    }
    return fakeRows;
  }
  let { data, error, count } = await (() => {
    let r = supabase
      .from('player_rank_hitting')
      .select(`*`)
      .eq('player_id', playerId)
      .order('year', { ascending: false });

    return r;
  })();

  const formattedData = hrParseData(data);

  return formattedData;
};
const {
  data: playerRankHitting,
  pending: playerRankHittingLoading,
  refresh: refreshPlayerRankHitting,
} = await useAsyncData(`player-rank-hitting`, getPlayerRankHittingData);
const statYearHitting = computed(() => {
  return playerRankHitting.value?.find((row) => row.year == statYear.value);
});
const barYearHitting = computed(() => {
  return playerRankHitting.value?.find((row) => row.year == barYear.value);
});
/**
 * Player Rank Pitching
 */
const PR_TABLE_NAMES: ValidTableName[] = [
  'pitching',
  'pitching_team',
  'player_rank',
];
const { parseData: prParseData, localDictionary: pitchingRankDictionary } =
  useDataDictionary(PR_TABLE_NAMES);
useDataDictionary(PR_TABLE_NAMES);
const defaultPitchingRankCols = (() => {
  const cols = [...pitchingColumnsDictionary].filter(
    (col) =>
      !['player_name', 'team_name', 'position', 'classification'].includes(
        col.key
      )
  );
  cols.splice(1, 0, {
    key: 'team_id',
    label: 'School',
    sortable: true,
    class: '!text-left',
    enabledByDefault: true,
  });
  return transformColumns(cols, pitchingRankDictionary.value);
})();
const selectedPitchingRankColumns = ref([...defaultPitchingRankCols]);
const pitchingRankCols = computed((): Column[] => {
  return defaultPitchingRankCols.filter((column) => {
    return selectedPitchingRankColumns.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedPitchingRankColumnsChange = ($event: Column[]) => {
  selectedPitchingRankColumns.value = $event;
};
/**
 * Player Rank Pitching
 */
const getPlayerRankPitchingData = async () => {
  if (!hasAccess.value) {
    const fakeRows = [];
    for (var i = 0; i < 4; i++) {
      const fakeRow = {} as Record<any, any>;
      pitchingRankCols.value?.map((col, index) => {
        fakeRow[col.key] =
          index == 0 ? 2024 : index == 1 ? 'Sign up for access ' : '0.000';
      });
      fakeRows.push(fakeRow);
    }
    return fakeRows;
  }
  let { data, error, count } = await (() => {
    let r = supabase
      .from('player_rank_pitching')
      .select(`*`)
      .eq('player_id', playerId)
      .order('year', { ascending: false });

    return r;
  })();

  const formattedData = prParseData(data);

  return formattedData;
};

const {
  data: playerRankPitching,
  pending: playerRankPitchingLoading,
  refresh: refreshPlayerRankPitching,
} = await useAsyncData(`player-rank-pitching`, getPlayerRankPitchingData);
const statYearPitching = computed(() => {
  return playerRankPitching.value?.find((row) => row.year == statYear.value);
});
const barYearPitching = computed(() => {
  return playerRankPitching.value?.find((row) => row.year == barYear.value);
});
/**
 * Player Details
 */
const playerQuery = supabase
  .from('players')
  .select(
    `
    *,
    hitting:hitting(*),
    pitching:pitching(*),
    fielding:fielding(*),
    team:team_id(id, name, sport, conference:conference_id(id, name, classification)),
    roster:rosters(*,team:teams(*)),
    drafts:drafts(*)
  `
  )
  .eq('id', playerId)
  .single();

type PlayerDetails = DbResultOk<typeof playerQuery>;
const { data: player } = await useAsyncData<PlayerDetails>(
  'player',
  async () => {
    const { data } = await playerQuery;
    return data;
  }
);

useHead({
  title: `${player.value?.player_name} | Detailed NCAA Baseball Player Analytics`,
  meta: [
    {
      name: 'description',
      content:
        'Discover in-depth analytics of NCAA baseball players. Review comprehensive stats, performance trends, and career projections.',
    },
  ],
});

const params = reactive<{
  gameStatsId: number | null;
  homeAway: '' | 'home' | 'away';
  conferenceClassification: 'upper' | 'lower' | 'middle' | '';
  opponentInOutConference: 'in' | 'out' | 'D-I' | 'D-II' | 'D-III' | '';
  dayOfWeek: 'weekdays' | 'weekends' | '';
  playerTeamId: number | null;
  playerConference: string | null;
  playsView: 'filters' | 'single-game';
  year: 2025 | 2024;
}>({
  gameStatsId: null,
  homeAway: '',
  conferenceClassification: '',
  opponentInOutConference: '',
  dayOfWeek: '',
  playerTeamId: null,
  playerConference: null,
  playsView: 'filters',
  year: 2025,
});
const reYearRosterTeam = computed(() => {
  return player.value?.roster?.find((r) => r.year == params.year);
});
watchEffect(() => {
  if (reYearRosterTeam?.value) {
    params.playerTeamId = reYearRosterTeam.value?.team_id;
    params.playerConference = reYearRosterTeam.value?.team.conference_id;
  }
});

/**
 * When player roster years change (should only be when component mounts),
 * update the bar and stat years to the highest roster year
 */
watchEffect(() => {
  if (player.value?.roster) {
    const highestRosterYear = player.value.roster.reduce((max, r) => {
      return Math.max(max, r.year);
    }, 0);
    barYear.value = highestRosterYear;
    statYear.value = highestRosterYear;
  }
});

const { data: playerPlays, isLoading: playerPlaysLoading } = usePlayerPlays(
  playerId,
  params,
  {
    enabled: computed(() => {
      return (
        hasCurrentPortalPass.value &&
        params.playsView === 'single-game' &&
        !!params.gameStatsId &&
        !!params.playerTeamId
      );
    }),
  }
);

const colorMode = useColorMode();

const currentPlayIndex = ref(0);

const sortedGroups = computed(() => {
  if (!filteredGamesPlays.value) return [];

  // Group plays by game and calculate sums
  const gameGroups = new Map();

  filteredGamesPlays.value.forEach((play) => {
    const game = teamGames.value?.games.find(
      (g) => g.game_stats_id === play.game_stats_id
    );
    if (!game) return;

    if (!gameGroups.has(game.game_stats_id)) {
      gameGroups.set(game.game_stats_id, {
        index: gameGroups.size + 1,
        game,
        plays: [],
        sum: 0,
      });
    }

    const group = gameGroups.get(game.game_stats_id);
    group.plays.push(play);
    group.sum += play.run_expectancy_change;
  });

  // Convert to arrays and sort by date
  return Array.from(gameGroups.values()).sort(
    (a, b) => new Date(a.game.date).getTime() - new Date(b.game.date).getTime()
  );
});

const reChartData = computed<ChartData<'line'>>(() => {
  if (params.playsView === 'filters') {
    let cumulativeSum = 0;
    const cumulativeSums = sortedGroups.value.map((group) => {
      cumulativeSum += group.sum;
      return cumulativeSum;
    });

    const cumulativeReColor =
      colorMode.value === 'dark'
        ? 'rgba(255, 255, 255, 0.5)'
        : 'rgba(0, 0, 0, 0.5)';
    const gameReColor = '#9c2b32';

    return {
      labels: sortedGroups.value.map((group, index) =>
        params.playsView === 'single-game'
          ? `${group.game.away_team_name} @ ${
              group.game.home_team_name
            } (${formatDate(group.game.date)})`
          : index + 1
      ),
      datasets: [
        {
          label: 'Game RE24',
          borderColor: gameReColor,
          backgroundColor: gameReColor,
          borderWidth: 1,
          data: sortedGroups.value.map((group) => group.sum),
          pointBackgroundColor: gameReColor,
          pointBorderColor: gameReColor,
          yAxisID: 'y',
        },
        params.playsView === 'filters'
          ? {
              label: 'Cumulative RE24',
              borderColor: cumulativeReColor,
              backgroundColor: cumulativeReColor,
              data: cumulativeSums,
              pointRadius: 0,
              borderWidth: 1,
              yAxisID: 'y1',
            }
          : null,
      ],
    };
  }

  // Single game view - keep existing play-by-play logic
  const cumulativeSum = filteredGamesPlays.value?.reduce((acc, play, index) => {
    const previousSum = index > 0 ? acc[index - 1] : 0;
    return [...acc, previousSum + play.run_expectancy_change];
  }, [] as number[]);

  return {
    labels: filteredGamesPlays.value?.map((play) =>
      play.play_text.substring(0, 100).replace(/3a/g, ';')
    ),
    datasets: [
      {
        label: 'Run Expectancy Change',
        borderColor:
          colorMode.value === 'dark'
            ? 'rgba(255, 255, 255, 0.5)'
            : 'rgba(0, 0, 0, 0.5)',

        borderWidth: 1,
        backgroundColor: 'transparent',
        data: filteredGamesPlays.value?.map(
          (play) => play.run_expectancy_change
        ),
        pointBackgroundColor: '#9c2b32',
        pointBorderColor: '#9c2b32',
        yAxisID: 'y',
      },
      params.playsView === 'filters'
        ? {
            label: 'Cumulative RE24',
            borderColor: '#9c2b32',
            backgroundColor: 'transparent',
            data: cumulativeSum,
            pointRadius: 0,
            borderWidth: 1,
            yAxisID: 'y1',
          }
        : null,
    ],
  };
});

const filteredGamesPlays = computed(() => {
  let plays = [];

  if (params.playsView === 'single-game') {
    plays = playerPlays.value?.plays;
  } else {
    teamGames.value?.games.forEach((game) => {
      plays = plays.concat(
        game.plays.filter((play) => play.player_id === parseInt(playerId))
      );
    });
  }

  return plays;
});

const playsStats = computed(() => {
  const plays = filteredGamesPlays.value || [];
  const runExpectancyValues = plays.map((play) => play.run_expectancy_change);
  const runExpectancySum = runExpectancyValues.reduce(
    (sum, val) => sum + val,
    0
  );
  // rounded to 2 decimal places
  const roundedRunExpectancySum = Math.round(runExpectancySum * 100) / 100;
  const totalPlays = plays.length;
  const avgRunExpectancyChange =
    totalPlays > 0 ? runExpectancySum / totalPlays : 0;

  // sum unique game ids
  const totalGames = plays.reduce((acc, play) => {
    if (!acc.includes(play.game_stats_id)) {
      acc.push(play.game_stats_id);
    }
    return acc;
  }, []).length;

  return {
    runExpectancySum,
    roundedRunExpectancySum,
    totalPlays,
    totalGames,
    avgRunExpectancyChange,
  };
});

const reUpsellChartData = {
  labels: [
    'Sign Up to View RE24',
    'Sign Up to View RE24',
    'Sign Up to View RE24',
    'Sign Up to View RE24',
    'Sign Up to View RE24',
  ],
  datasets: [
    {
      label: 'Portal Pass Required - Sign Up to View',
      borderColor: 'rgba(255, 255, 255, 0.35)',
      backgroundColor: 'transparent',
      data: [-0.257, -0.16, -1.033, 0.791, 1],
      pointBackgroundColor: '#9c2b32',
      pointBorderColor: '#9c2b32',
    },
  ],
};

const showxAxes = computed(() => {
  return true;
  if (params.playsView === 'single-game') {
    return filteredGamesPlays.value?.length < 30;
  } else if (params.playsView === 'filters') {
    return filteredGamesPlays.value?.length < 20;
  }
  return false;
});

const reChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    tooltip: {
      callbacks: {
        title: function (context) {
          if (!hasCurrentPortalPass.value) return 'Portal Pass Required';

          if (params.playsView === 'single-game') {
            return;
          }

          // this function renders everything we need for the tooltip, this so we only need to run it for the firt dataset
          // if (context.datasetIndex !== 0) return;

          const gameGroup = sortedGroups.value[context[0].dataIndex];
          if (!gameGroup) return '';

          // Game details and score
          const game = gameGroup.game;
          const isHome = game.home_team_id === reYearRosterTeam.value?.team_id;
          const score = `${game.away_team_score}-${game.home_team_score}`;
          const matchup = isHome
            ? `${game.away_team_name} @ ${game.home_team_name}`
            : `@ ${game.home_team_name}`;

          return `${formatDate(gameGroup.game.date)} - ${matchup} (${score})`;
        },
      },
    },
  },
  scales: {
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      title: {
        display: true,
        text:
          params.playsView === 'filters'
            ? 'Game RE24'
            : 'Individual RE24 Change',
        color:
          colorMode.value === 'dark'
            ? 'rgba(255, 255, 255, 1)'
            : 'rgba(0, 0, 0, 1)',
      },
      border: {
        dash: [5, 5],
      },
      ticks: {
        color:
          colorMode.value === 'dark'
            ? 'rgba(255, 255, 255, 1)'
            : 'rgba(0, 0, 0, 1)',
      },
      grid: {
        color:
          colorMode.value === 'dark'
            ? 'rgba(255, 255, 255, 0.25)'
            : 'rgba(0, 0, 0, 0.5)',
      },
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      title: {
        display: true,
        text: 'Cumulative RE24',
        color:
          colorMode.value === 'dark'
            ? 'rgba(255, 255, 255, 1)'
            : 'rgba(0, 0, 0, 1)',
      },
      grid: {
        drawOnChartArea: false,
      },
      ticks: {
        color:
          colorMode.value === 'dark'
            ? 'rgba(255, 255, 255, 1)'
            : 'rgba(0, 0, 0, 1)',
      },
    },
    x: {
      display: showxAxes.value,
      position: 'bottom',
      ticks: {
        color:
          colorMode.value === 'dark'
            ? 'rgba(255, 255, 255, 1)'
            : 'rgba(0, 0, 0, 1)',
        maxRotation: 0,
        minRotation: 0,
        autoSkip: true,
        callback: function (val, index) {
          if (!hasCurrentPortalPass.value) {
            return 'Sign up to view RE24';
          }

          if (params.playsView === 'filters') {
            const labels = reChartData.value.labels || [];
            return labels[index];
          }

          // Single game view - keep existing play-by-play logic
          // replace character '3a' with ';'
          const labels =
            filteredGamesPlays.value?.map((play) =>
              play.play_text.substring(0, 100).replace(/3a/g, ';')
            ) || [];
          const label = labels[index];
          if (!label) return '';

          const ctx = document.createElement('canvas').getContext('2d');
          if (!ctx) return label;

          const words = label.split(' ');
          const lines = [];
          let currentLine = words[0];
          let isTruncated = false;

          for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const width = ctx.measureText(currentLine + ' ' + word).width;

            if (width < 160) {
              currentLine += ' ' + word;
            } else {
              if (lines.length > 0) {
                isTruncated = true;
                break;
              }
              lines.push(currentLine);
              currentLine = word;
            }
          }

          if (isTruncated) {
            currentLine += '...';
          }
          lines.push(currentLine);

          return lines;
        },
      },
      border: {
        dash: [5, 5],
      },
      grid:
        params.playsView === 'single-game'
          ? {
              color:
                colorMode.value === 'dark'
                  ? 'rgba(255, 255, 255, 0.25)'
                  : 'rgba(0, 0, 0, 0.5)',
            }
          : {
              display: false,
            },
    },
    x2: {
      display: params.playsView === 'single-game',
      position: 'top',
      ticks: {
        color:
          colorMode.value === 'dark'
            ? 'rgba(255, 255, 255, 1)'
            : 'rgba(0, 0, 0, 0.5)',
        maxRotation: 0,
        minRotation: 0,
        autoSkip: false,
        callback: function (val, index) {
          if (!hasCurrentPortalPass.value) {
            return 'Sign up to view RE24';
          }
          const play = filteredGamesPlays.value?.[index];
          return `Play ${index + 1} - Inning: ${play.inning}`;
        },
      },
      grid: {
        display: false,
      },
    },
  },
}));

const {
  data: teamGames,
  isLoading: teamGamesLoading,
  isRefetching: teamGamesRefetching,
} = useTeamGames(
  computed(() => reYearRosterTeam.value?.team_id),
  computed(() =>
    params.playsView == 'filters'
      ? params
      : { year: params.year, gameStatsId: params.gameStatsId }
  ),
  {
    enabled: computed(
      () => hasCurrentPortalPass.value && !!player.value?.team_id
    ),
  }
);

// // Add watcher for playerPlays.value.plays
// watchEffect(() => {
//   if (
//     teamGames.value &&
//     teamGames.value.games?.length > 0 &&
//     !params.gameStatsId
//   ) {
//     params.gameStatsId = teamGames.value.games[0].game_stats_id;
//   }
// });

const teamGameOptions = computed(() => {
  return teamGames.value?.games?.map((game, index) => {
    return {
      label: `Game ${(teamGames.value?.games ?? []).indexOf(game) + 1}/${
        teamGames.value?.games?.length ?? 0
      } - ${game.away_team_name} @ ${game.home_team_name}`,
      value: game.game_stats_id,
    };
  });
});

const hittingStats = computed(() => {
  const statsYears = player?.value?.hitting?.sort(
    (a, b) => (b.year || 0) - (a.year || 0)
  );
  if (!statsYears) return [];
  const data = hittingParseData(statsYears);
  return data;
});
const advancedHittingStats = computed(() => {
  if (!hasAccess.value) {
    const fakeRows = [];
    for (var i = 0; i < 4; i++) {
      const fakeRow = {} as Record<any, any>;
      advancedHittingCols.value?.map((col, index) => {
        fakeRow[col.key] =
          index == 0 ? 2024 : index == 1 ? 'Sign up for access ' : '0.000';
      });
      fakeRows.push(fakeRow);
    }
    return fakeRows;
  }
  const statsYears = player?.value?.hitting?.sort(
    (a, b) => (b.year || 0) - (a.year || 0)
  );
  if (!statsYears) return [];
  const data = hittingParseData(statsYears);
  return data;
});
const pitchingStats = computed(() => {
  const statsYears = player?.value?.pitching?.sort(
    (a, b) => (b.year || 0) - (a.year || 0)
  );
  if (!statsYears) return [];
  const data = pitchingParseData(statsYears || []);
  return data;
});
const advancedPitchingStats = computed(() => {
  if (!hasAccess.value) {
    const fakeRows = [];
    for (var i = 0; i < 4; i++) {
      const fakeRow = {} as Record<any, any>;
      advancedPitchingCols.value?.map((col, index) => {
        fakeRow[col.key] =
          index == 0 ? 2024 : index == 1 ? 'Sign up for access ' : '0.000';
      });
      fakeRows.push(fakeRow);
    }
    return fakeRows;
  }
  const statsYears = player?.value?.pitching?.sort(
    (a, b) => (b.year || 0) - (a.year || 0)
  );
  if (!statsYears) return [];
  const data = pitchingParseData(statsYears);
  return data;
});
const fieldingStats = computed(() => {
  const statsYears = player?.value?.fielding?.sort(
    (a, b) => (b.year || 0) - (a.year || 0)
  );
  if (!statsYears) return [];
  const data = fieldingParseData(statsYears || []);
  return data;
});

const canGoToPreviousGame = computed(() => {
  if (!teamGames.value?.games || !params.gameStatsId) return false;
  const currentIndex = teamGames.value.games.findIndex(
    (game) => game.game_stats_id === params.gameStatsId
  );
  return currentIndex > 0;
});

const canGoToNextGame = computed(() => {
  if (!teamGames.value?.games || !params.gameStatsId) return false;
  const currentIndex = teamGames.value.games.findIndex(
    (game) => game.game_stats_id === params.gameStatsId
  );
  return currentIndex < teamGames.value.games.length - 1;
});

const goToPreviousGame = () => {
  if (!teamGames.value?.games || !params.gameStatsId) return;
  const currentIndex = teamGames.value.games.findIndex(
    (game) => game.game_stats_id === params.gameStatsId
  );
  if (currentIndex > 0) {
    params.gameStatsId = teamGames.value.games[currentIndex - 1].game_stats_id;
  }
};

const goToNextGame = () => {
  if (!teamGames.value?.games || !params.gameStatsId) return;
  const currentIndex = teamGames.value.games.findIndex(
    (game) => game.game_stats_id === params.gameStatsId
  );
  if (currentIndex < teamGames.value.games.length - 1) {
    params.gameStatsId = teamGames.value.games[currentIndex + 1].game_stats_id;
  }
};

const gameSearchRef = ref<HTMLDivElement | null>(null);

const viewPlayByPlay = (gameId: number) => {
  params.gameStatsId = gameId;
  params.playsView = 'single-game';
  // Wait for next tick to ensure component updates
  console.log(gameSearchRef.value);
  nextTick(() => {
    gameSearchRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  });
};
</script>

<style lang="scss">
.radio-filter-group {
  display: block;
  @media screen and (min-width: 768px) {
    display: flex;
  }
  &__label {
    @media screen and (min-width: 768px) {
      min-width: 200px;
    }
  }
  [aria-checked='true'] {
    .radio-filter-group__option {
      @apply dark:bg-transparent border border-neutral-400 text-white;
    }
  }
}
</style>
